/**
 * Utility functions for text processing and direction detection
 */

/**
 * Detect if text contains Hebrew characters
 */
export function containsHebrew(text: string): boolean {
  // Hebrew Unicode range: \u0590-\u05FF
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
}

/**
 * Detect text direction based on content
 * Uses percentage-based detection to avoid false positives from RTL author names in English text
 * Returns 'rtl' if more than 30% of meaningful characters are Hebrew or Arabic, 'ltr' otherwise
 */
export function detectTextDirection(text: string): 'rtl' | 'ltr' {
  if (!text || text.trim().length === 0) return 'ltr';
  
  const hebrewPattern = /[\u0590-\u05FF]/g;
  const arabicPattern = /[\u0600-\u06FF]/g;
  
  const hebrewMatches = text.match(hebrewPattern);
  const arabicMatches = text.match(arabicPattern);
  const rtlCharCount = (hebrewMatches ? hebrewMatches.length : 0) + (arabicMatches ? arabicMatches.length : 0);
  
  // Count meaningful characters (letters, numbers, excluding whitespace and punctuation)
  const meaningfulChars = text.match(/[\p{L}\p{N}]/gu);
  const totalMeaningfulChars = meaningfulChars ? meaningfulChars.length : 0;
  
  if (totalMeaningfulChars === 0) return 'ltr';
  
  // Calculate RTL (Hebrew + Arabic) percentage
  const rtlPercentage = (rtlCharCount / totalMeaningfulChars) * 100;
  
  // Use threshold: if more than 30% of meaningful characters are RTL, mark as RTL
  // This prevents RTL author names from making English text appear as RTL
  const RTL_THRESHOLD = 30;
  
  return rtlPercentage > RTL_THRESHOLD ? 'rtl' : 'ltr';
}

